import { partition } from 'lodash';
import pRetry from 'p-retry';
import { AdditionalInfoFormViewNext, AdditionalInfoView } from '../../../types';
import { useAdditionalInfosResource } from '../use-additional-infos-event-resource';
import { createStreamListener } from './helpers';
import { AdditionalInfosController } from './types';

const EVENT_NUMBER_REGEX = /(#[\w]{3,})/;

export interface CreateEventControllerConfig {
    entityId: number;
    resource: ReturnType<typeof useAdditionalInfosResource>;
    pageSize: number;
    includeEventNarratives: boolean;
}

export function createEventController(
    config: CreateEventControllerConfig
): AdditionalInfosController {
    const { entityId, resource, pageSize, includeEventNarratives } = config;

    return {
        async loadPage({ cursor, listDirection, filter }) {
            return pRetry(
                async () => {
                    const response = await resource.getInfosByCursor({
                        agencyEventId: entityId,
                        limit: pageSize,
                        cursor,
                        direction: listDirection,
                        filter,
                        includeEventNarratives,
                    });
                    const data = response.data.data;
                    if (!data) {
                        return {
                            additionalInfos: [],
                            pagination: { hasNextPage: false },
                            agencyEventNumberToIdMap: {},
                        };
                    }
                    const pagination = data.pagination ?? {
                        hasNextPage: false,
                    };
                    return {
                        additionalInfos: data.additionalInfos ?? [],
                        pagination,
                        agencyEventNumberToIdMap: data.agencyEventNumberToIdMap ?? {},
                    };
                },
                { retries: 2 }
            );
        },

        async createInfo(info: AdditionalInfoFormViewNext) {
            // currently additional-info creation endpoint will not return 'agencyEventNumberToIdMap'.
            // when a new additional-info is created, stream/nudge will indicate `infosToFetch`, then we call `fetchAdditionalInfosById` to get additional-infos with `agencyEventNumberToIdMap`
            // Once createAdditionalInfo return with 'agencyEventNumberToIdMap', we can call `upsertAgencyEventNumberToIdMap` immediately here
            const result = await resource.createAdditionalInfo(info);
            const view = result?.data?.data?.cadNextAdditionalInfoView as AdditionalInfoView;
            return view ? [view] : [];
        },

        async deleteInfo(infoId: number) {
            await resource.deleteInfo(entityId, infoId);
        },

        createStreamListeners({ store, currentUserId, fetchNextPage }) {
            const shouldReloadAdditionalInfos = (events: number[] | undefined): boolean => {
                return Array.isArray(events) && events.length > 0 && events.includes(entityId);
            };

            const getAdditionalInfoIdsToEvict = (
                infos: { agencyEventId: number; id: number }[]
            ): number[] => {
                if (!Array.isArray(infos) || infos.length === 0) return [];
                return infos
                    .filter(
                        (info) =>
                            info.agencyEventId === entityId && store.listStore.hasItem(info.id)
                    )
                    .map((info) => info.id);
            };

            const getAdditionalInfoIdsToInsert = (
                infos: AdditionalInfoView[],
                filterTypes: string[]
            ): AdditionalInfoView[] => {
                if (!Array.isArray(infos) || infos.length === 0) return [];
                return infos.filter((info) => {
                    if (store.listStore.getItemById(info.id)) return false;
                    const matchesAgencyEventId =
                        info.agencyEventId === entityId ||
                        (info.parentAgencyEventId && info.parentAgencyEventId === entityId);
                    const matchesFilter = filterTypes.includes(info.type);
                    return matchesAgencyEventId && matchesFilter;
                });
            };

            const fetchAdditionalInfosById = async (infos: AdditionalInfoView[]): Promise<void> => {
                const response = await resource.getInfosByIds(entityId, {
                    ids: infos.map((info) => info.id),
                });
                const data = response.data.data;
                if (data?.additionalInfos) {
                    store.upsertInfos(data.additionalInfos, true);
                }
                if (data?.agencyEventNumberToIdMap) {
                    store.upsertAgencyEventNumberToIdMap(data.agencyEventNumberToIdMap);
                }
            };

            const infos = createStreamListener({
                streamNames: ['infos'],
                onMessage: (message) => {
                    if (shouldReloadAdditionalInfos(message.payload.reset)) {
                        fetchNextPage({ reset: true });
                        return;
                    }

                    const infoIdsToEvict = getAdditionalInfoIdsToEvict(
                        message.payload.delete ?? []
                    );
                    if (infoIdsToEvict.length > 0) {
                        store.removeInfos(infoIdsToEvict);
                    }

                    const infosToInsert = getAdditionalInfoIdsToInsert(
                        message.payload.create ?? [],
                        store.filter ?? []
                    ).filter((info) => !infoIdsToEvict.includes(info.id));

                    const infosToAdd = infosToInsert.filter((info) => info.text);
                    const infosToFetch = infosToInsert.filter(
                        (info) => !info.text || EVENT_NUMBER_REGEX.test(info.text)
                    );

                    if (infosToAdd.length > 0) {
                        const [ownInfos, otherInfos] = partition(
                            infosToAdd,
                            (info) => info.authorUserId === currentUserId
                        );
                        store.upsertInfos(otherInfos, true);
                        store.upsertInfos(ownInfos, false);
                    }

                    if (infosToFetch.length > 0) {
                        fetchAdditionalInfosById(infosToFetch);
                    }
                },
            });

            return { infos };
        },

        createFilterDirective(crossClassEventsIds) {
            // TODO: When an FR user is assigned to another event, there is a brief moment when `loadPage` function runs for the old assigned event,
            // which made a mix of correct additional info messages (for the new event) and incorrect ones (for the old event) be displayed in FR
            // Assigned Event page. To make sure that does not happen, we are currently using this filter directive. It is not ideal, but other
            // approaches to fix that might have the potential to cause other, maybe even worse bugs.
            const allRelevantEventsIds = [...crossClassEventsIds, entityId];
            return (item: AdditionalInfoView): boolean => {
                if (
                    item.parentAgencyEventId &&
                    allRelevantEventsIds.includes(item.parentAgencyEventId)
                ) {
                    return true;
                }
                return allRelevantEventsIds.includes(item.agencyEventId);
            };
        },
    };
}
